import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';
import { queryKeys } from '@/lib/queryKeys';

/**
 * 홈 TO DO 할일 — 서버에 저장되는 개인 할일.
 *
 * 목(`mockList`)을 거치지 않고 직접 호출한다. 목 계층은 "표 하나 = 표준 CRUD" 를 전제로 만들어졌는데,
 * 할일은 완료 체크가 전용 엔드포인트(PATCH .../done)이고 목록에 **저장되지 않는 파생 항목**이 섞여 온다.
 * 그 둘을 목 계층에 우겨넣으면 규칙이 표마다 갈라진다.
 */

/**
 * 서버가 내려주는 할일 한 줄.
 *
 * `kind` 로 할 수 있는 일이 갈린다 — 화면이 내용을 보고 짐작하지 않게 서버가 분류해 준다.
 *  - `MANUAL`   본인 메모. 체크·삭제 가능
 *  - `ASSIGNED` 배정받은 일. 체크·삭제 가능(서버는 지우지 않고 가린다 — V230). 내용 수정만 안 된다
 *  - `DERIVED`  자격증 만료 등. 저장된 행이 아니라 체크는 못 하고, 치우는 것만 된다(V231)
 */
export type ServerTodo = {
  id: string;
  kind: 'MANUAL' | 'ASSIGNED' | 'DERIVED';
  content: string;
  dueDate: string | null;
  done: boolean;
  /** 완료로 체크한 시각(ISO). 전체보기의 <b>일자별 축</b>이 이 값으로 하루를 묶는다(DOC-100 §4.1). */
  doneAt: string | null;
  /**
   * 적은 시각(ISO). 위젯이 최신을 맨 위로 세우는 데만 쓴다(DOC-123). 파생은 null, 서버 재시작 전
   * 응답에는 없을 수 있어 옵셔널이다 — 없으면 서버 순서 그대로 아래에 남는다.
   */
  createdAt?: string | null;
  requestId: string | null;
  href: string | null;
  /**
   * 기간의 시작 · 진행률 · 분류 · 현장 (DOC-100 §2 · `V367`).
   *
   * <p><b>옵셔널로 둔다.</b> 배포 직후의 캐시된 응답에는 없을 수 있고, 그때 화면은 「기간이 안 적힌
   * 줄」 로 그린다 — 없는 값을 {@link dueDate} 로 메워 하루짜리 막대를 세우지 않는다. 열흘 걸린
   * 일이 간트에서 하루로 보이고, 그 그림이 그대로 보고로 나간다.
   */
  startDate?: string | null;
  /** 0~100. 서버가 `done` 과 맞춘다 — 체크하면 100, 100 을 적으면 완료다. */
  progress?: number | null;
  category?: string | null;
  siteId?: string | null;
  siteName?: string | null;
  /** 중요도(사용자 지시 2026-09-30). 켜면 목록 왼쪽에 ★ 가 선다. */
  important?: boolean;
  /**
   * 목록에서 치울 수 있는가(V264). 서버가 정한다 — 화면이 id 앞머리로 판단하면 같은 규칙이 두 곳에
   * 살고, 한쪽만 고친 날 「버튼은 보이는데 눌러도 안 되는」 줄이 된다.
   *
   * 지금 false 인 줄은 **교육 증빙 제출** 하나다. 회사가 나에게 줄 돈을 가리키는 줄이라
   * 「지금 안 한다」를 고를 수 있는 성격이 아니다(사용자 지시 2026-09-09).
   *
   * 옵셔널로 둔 것은 옛 응답(필드가 없는)을 받는 순간을 위해서다 — 없으면 치울 수 있다.
   */
  dismissible?: boolean;
};

export function useTodos() {
  return useQuery({
    queryKey: queryKeys.todoItems,
    queryFn: () => apiFetch<ServerTodo[]>('/v1/home/todos'),
  });
}

/**
 * 치운 파생 할일의 키(V231).
 *
 * 서버가 만드는 파생은 목록에서 이미 빠져서 온다. 이 목록이 필요한 것은 **화면이 스스로 계산하는**
 * 파생(결재 대기·주간보고 미작성·미처리 요청) 때문이다 — 서버는 그 줄들의 존재를 모르므로 걸러 줄 수 없다.
 */
export function useDismissedTodoKeys() {
  return useQuery({
    queryKey: queryKeys.todoDismissals,
    queryFn: () => apiFetch<string[]>('/v1/home/todos/dismissals'),
  });
}

/** 추가·체크·삭제가 모두 목록 하나를 다시 그리게 한다. 낙관적 갱신은 두지 않는다 — 위젯이 작다. */
export function useTodoMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKeys.todoItems });

  // 기간·분류·중요도는 전체보기의 「업무 추가」 창이 채워 온다. 위젯은 내용·기한만 넘기고
  // 나머지는 여기서 빈 값이 된다 — 새로 만드는 줄이라 «비운다» 와 «안 보낸다» 가 같은 뜻이다.
  const add = useMutation({
    mutationFn: (input: {
      content: string;
      dueDate?: string | null;
      startDate?: string | null;
      progress?: number | null;
      category?: string | null;
      siteId?: string | null;
      important?: boolean;
    }) =>
      apiFetch<ServerTodo>('/v1/home/todos', {
        method: 'POST',
        body: {
          content: input.content,
          dueDate: input.dueDate ?? null,
          startDate: input.startDate ?? null,
          progress: input.progress ?? null,
          category: input.category ?? null,
          siteId: input.siteId ?? null,
          important: input.important ?? false,
        },
      }),
    onSuccess: invalidate,
  });

  /**
   * 한 줄을 고친다 — 전체보기의 「업무 수정」 창과 목록의 ★ 가 쓴다(DOC-100 §4.4).
   *
   * <p><b>늘 전부 보낸다.</b> 서버는 일정 칸이 하나라도 오면 그 묶음을 통째로 앉히므로
   * ({@code SaveTodoRequest.hasSchedule}), 별만 켜려고 일부만 보내면 나머지가 비워진다.
   *
   * <p>배정받은 줄은 내용·기한이 잠긴다 — 바꾸려 들면 서버가 `TODO_ASSIGNED_READONLY` 로 답하고,
   * 그대로 되보내는 것(별을 누를 때)은 통과한다.
   */
  const edit = useMutation({
    mutationFn: (input: {
      id: string;
      content: string;
      dueDate?: string | null;
      startDate?: string | null;
      progress?: number | null;
      category?: string | null;
      siteId?: string | null;
      important?: boolean;
    }) =>
      apiFetch<ServerTodo>(`/v1/home/todos/${input.id}`, {
        method: 'PUT',
        body: {
          content: input.content,
          dueDate: input.dueDate ?? null,
          startDate: input.startDate ?? null,
          progress: input.progress ?? null,
          category: input.category ?? null,
          siteId: input.siteId ?? null,
          important: input.important ?? false,
        },
      }),
    onSuccess: invalidate,
  });

  const setDone = useMutation({
    mutationFn: (input: { id: string; done: boolean }) =>
      apiFetch<ServerTodo>(`/v1/home/todos/${input.id}/done`, {
        method: 'PATCH',
        body: { done: input.done },
      }),
    onSuccess: invalidate,
  });

  // 저장된 행(uuid)과 파생 키(`license:...`)를 같은 경로로 보낸다 — 무엇을 지우고 무엇을 가릴지는
  // 서버가 안다(TodoService.delete). 키에 `:` 가 들어가므로 인코딩한다.
  const remove = useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/v1/home/todos/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: queryKeys.todoDismissals });
    },
  });

  return { add, edit, setDone, remove };
}

/**
 * 공지 등에서 "할일로 보내기" 를 켰을 때의 입력값. 화면(TodoAssignFields)과 저장하는 쪽이
 * 함께 쓰므로 컴포넌트 파일이 아니라 여기 둔다.
 */
export type TodoAssignValue = {
  enabled: boolean;
  dueDate: string;
  targets: TodoTarget[];
};

export const EMPTY_ASSIGNMENT: TodoAssignValue = { enabled: false, dueDate: '', targets: [] };

/** 배정 한 건 + 진행률. 요청 화면이 "시킨 것" 을 되돌아볼 때 쓴다. */
export type TodoAssignment = {
  id: string;
  content: string;
  dueDate: string | null;
  targets: string[];
  deliveredCount: number;
  doneCount: number;
};

export type TodoTarget = {
  type: 'ALL' | 'DEPARTMENT' | 'SITE' | 'PERSON' | 'USER';
  /** 부서·현장은 이름, PERSON 은 인력 id, USER 는 계정 id. 전사는 비운다. */
  key?: string | null;
};

/**
 * 대상에게 기한 있는 할일을 보낸다.
 *
 * `originType: 'NOTICE'` + `originId` 를 주면 그 공지에서 배포한 것으로 기록된다.
 * 전사 배포는 서버가 관리자만 통과시킨다(TODO_ALL_TARGET_FORBIDDEN).
 */
export function useAssignTodo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      content: string;
      dueDate?: string | null;
      targets: TodoTarget[];
      originType?: 'NOTICE';
      originId?: string;
    }) =>
      apiFetch<TodoAssignment>('/v1/todo-requests', {
        method: 'POST',
        body: {
          content: input.content,
          dueDate: input.dueDate ?? null,
          targets: input.targets.map((target) => ({ type: target.type, key: target.key ?? null })),
          originType: input.originType ?? null,
          originId: input.originId ?? null,
        },
      }),
    // 보낸 사람이 자기 할일 목록에 포함될 수 있다(부서 배포 등). 목록을 다시 읽는다.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.todoItems }),
  });
}
